/**
 * Admin — users. Mobile: search + role/status filter chips into the
 * per-user detail screen (unchanged behaviour). Desktop (≥1024, web): full
 * console table — checkbox column, search, role/wing/pending filters, real
 * bulk deactivate/reactivate, society + role-breakdown panels
 * ("Admin users" frame). Wired: GET /api/admin/users, PATCH
 * /api/admin/users/<id> (used both per-row and for bulk actions).
 * Port of screens-admin.jsx AdminUsers.
 *
 * DEVIATION: the frame's "Billing — who's due" panel has no backing
 * aggregate-billing endpoint (payments.py only exposes per-resident bill
 * routes) — replaced with a real role-breakdown panel computed from the
 * loaded user list. "+ Add resident" has no admin-invite endpoint either
 * (only self-serve onboarding/signup) — rendered disabled, same TODO(wire)
 * convention already used by app/login.tsx's OAuth buttons.
 */
import { router } from 'expo-router';
import React from 'react';
import { Platform, Pressable, View, useWindowDimensions } from 'react-native';

import { radius, space } from '@bero/tokens';
import { bool, str, useGateList } from '@/components/gate/api';
import { AdminSidebar, goBack } from '@/features/account-admin/bits';
import { Icon } from '@/features/account-admin/icons';
import { ChipsRow } from '@/features/community/bits';
import { api } from '@/lib/client';
import { useTheme } from '@/theme';
import { AppText, Avatar, Card, Chip, EmptyState, Hairline, Input, Pill, Screen, Skeleton, TopBar } from '@/ui';

const WIDE = 1024;

type Row = {
  id: string; name: string; email: string; flat: string; role: string;
  residentType: string; active: boolean; approved: boolean;
};

const FILTERS = ['All', 'Owners', 'Tenants', 'Deactivated'] as const;
type Filter = (typeof FILTERS)[number];

const ROLE_FILTERS = ['All roles', 'Owners', 'Tenants', 'Family', 'Admins'] as const;
type RoleFilter = (typeof ROLE_FILTERS)[number];

/** flat comes back nested as {id, code, bhk} on GET /api/admin/users — read
 * both shapes so a plain string (some legacy fixtures) still works. */
function flatCode(u: unknown): string {
  const direct = str(u, 'flat', 'flatCode');
  if (direct) return direct;
  if (u && typeof u === 'object') {
    const nested = (u as Record<string, unknown>).flat;
    if (nested && typeof nested === 'object') {
      const code = (nested as Record<string, unknown>).code;
      if (typeof code === 'string') return code;
    }
  }
  return '';
}

function toRow(u: unknown, i: number): Row {
  return {
    id: str(u, 'id', 'userId') ?? `u${i}`,
    name: str(u, 'name', 'fullName') ?? 'Resident',
    email: str(u, 'email') ?? '',
    flat: flatCode(u),
    role: str(u, 'role') ?? '',
    residentType: (str(u, 'residentType', 'type') ?? '').toUpperCase(),
    active: bool(u, 'active', 'isActive') ?? true,
    approved: bool(u, 'approved', 'isApproved') ?? true,
  };
}

function roleLabel(r: Row): string {
  if (r.role === 'MASTER_ADMIN') return 'Master admin';
  if (r.role === 'SUB_ADMIN') return 'Sub admin';
  if (r.role === 'GUARD') return 'Guard';
  if (r.residentType === 'OWNER') return 'Owner';
  if (r.residentType === 'TENANT') return 'Tenant';
  if (r.residentType === 'FAMILY') return 'Family';
  return r.residentType || 'Resident';
}

function wingOf(r: Row): string {
  const c = r.flat.trim();
  return /^[A-Za-z]/.test(c) ? c[0].toUpperCase() : '';
}

export default function AdminUsersScreen() {
  const { colors } = useTheme();
  const { width } = useWindowDimensions();
  const wide = Platform.OS === 'web' && width >= WIDE;
  const { items, state, reload } = useGateList<unknown>('/api/admin/users', 'users');
  const allRows = React.useMemo(() => items.map(toRow), [items]);

  const [q, setQ] = React.useState('');
  const [filter, setFilter] = React.useState<Filter>('All');

  const query = q.trim().toLowerCase();
  const mobileRows = allRows
    .filter((r) => {
      if (filter === 'Owners') return r.residentType === 'OWNER';
      if (filter === 'Tenants') return r.residentType === 'TENANT';
      if (filter === 'Deactivated') return !r.active;
      return true;
    })
    .filter((r) => !query || r.name.toLowerCase().includes(query) || r.flat.toLowerCase().includes(query));

  /* ---------------- desktop-only filter state ---------------- */
  const [roleFilter, setRoleFilter] = React.useState<RoleFilter>('All roles');
  const [wingFilter, setWingFilter] = React.useState<string>('All wings');
  const [pendingOnly, setPendingOnly] = React.useState(false);
  const [selected, setSelected] = React.useState<Set<string>>(new Set());
  const [bulkBusy, setBulkBusy] = React.useState(false);

  const wings = React.useMemo(
    () => Array.from(new Set(allRows.map(wingOf).filter(Boolean))).sort(),
    [allRows],
  );

  const desktopRows = allRows
    .filter((r) => {
      if (roleFilter === 'Owners') return r.residentType === 'OWNER';
      if (roleFilter === 'Tenants') return r.residentType === 'TENANT';
      if (roleFilter === 'Family') return r.residentType === 'FAMILY';
      if (roleFilter === 'Admins') return r.role === 'SUB_ADMIN' || r.role === 'MASTER_ADMIN';
      return true;
    })
    .filter((r) => wingFilter === 'All wings' || wingOf(r) === wingFilter)
    .filter((r) => !pendingOnly || !r.approved)
    .filter((r) => !query || r.name.toLowerCase().includes(query) || r.flat.toLowerCase().includes(query));

  const toggleSelected = (id: string) => setSelected((s) => {
    const next = new Set(s);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });

  const bulkSetActive = async (active: boolean) => {
    if (selected.size === 0 || bulkBusy) return;
    setBulkBusy(true);
    await Promise.allSettled(
      Array.from(selected).map((id) => api.patch(`/api/admin/users/${id}`, { isActive: active })),
    );
    setSelected(new Set());
    setBulkBusy(false);
    void reload();
  };

  const roleCounts = React.useMemo(() => {
    const c = { Owner: 0, Tenant: 0, Family: 0, Admin: 0, Guard: 0 };
    for (const r of allRows) {
      if (r.role === 'SUB_ADMIN' || r.role === 'MASTER_ADMIN') c.Admin++;
      else if (r.role === 'GUARD') c.Guard++;
      else if (r.residentType === 'OWNER') c.Owner++;
      else if (r.residentType === 'TENANT') c.Tenant++;
      else if (r.residentType === 'FAMILY') c.Family++;
    }
    return c;
  }, [allRows]);

  const flatsCount = React.useMemo(() => new Set(allRows.map((r) => r.flat).filter(Boolean)).size, [allRows]);

  /* ---------------- desktop console table ---------------- */
  if (wide) {
    return (
      <View style={{ flex: 1, flexDirection: 'row', backgroundColor: colors.canvas0 }}>
        <AdminSidebar />
        <View style={{ flex: 1 }}>
          <View style={{
            paddingHorizontal: space.s6, paddingVertical: space.s4, borderBottomWidth: 0.5,
            borderBottomColor: colors.borderSoft, flexDirection: 'row', alignItems: 'center',
            gap: space.s3, flexWrap: 'wrap',
          }}>
            <AppText variant="headingLg">Users</AppText>
            <View style={{ width: 260 }}>
              <Input value={q} onChangeText={setQ} placeholder="Search name, flat, email…" />
            </View>
            {ROLE_FILTERS.map((f) => (
              <Chip key={f} label={f} active={roleFilter === f} onPress={() => setRoleFilter(f)} />
            ))}
            <Chip label="All wings" active={wingFilter === 'All wings'} onPress={() => setWingFilter('All wings')} />
            {wings.map((w) => (
              <Chip key={w} label={`Wing ${w}`} active={wingFilter === w} onPress={() => setWingFilter(w)} />
            ))}
            <Chip label="Pending only" active={pendingOnly} onPress={() => setPendingOnly((v) => !v)} />
            <View style={{ marginLeft: 'auto', flexDirection: 'row', gap: space.s2 }}>
              {selected.size > 0 ? (
                <>
                  <AppText variant="bodySm" tone="tertiary" style={{ alignSelf: 'center' }}>{selected.size} selected</AppText>
                  <Pressable
                    disabled={bulkBusy}
                    onPress={() => { void bulkSetActive(true); }}
                    style={{ height: 40, paddingHorizontal: space.s3, borderRadius: radius.md, borderWidth: 1, borderColor: colors.border, justifyContent: 'center', opacity: bulkBusy ? 0.5 : 1 }}
                  >
                    <AppText variant="bodySm" weight="600">Reactivate</AppText>
                  </Pressable>
                  <Pressable
                    disabled={bulkBusy}
                    onPress={() => { void bulkSetActive(false); }}
                    style={{ height: 40, paddingHorizontal: space.s3, borderRadius: radius.md, borderWidth: 1, borderColor: colors.border, justifyContent: 'center', opacity: bulkBusy ? 0.5 : 1 }}
                  >
                    <AppText variant="bodySm" weight="600" style={{ color: colors.danger500 }}>Deactivate</AppText>
                  </Pressable>
                </>
              ) : null}
              {/* TODO(wire): no admin-initiated resident-creation endpoint yet — only self-serve onboarding/signup exists. */}
              <View
                accessibilityLabel="Add resident — coming soon"
                style={{ height: 40, paddingHorizontal: space.s3, borderRadius: radius.md, backgroundColor: colors.canvas2, justifyContent: 'center', opacity: 0.5 }}
              >
                <AppText variant="bodySm" weight="600">+ Add resident</AppText>
              </View>
            </View>
          </View>

          <View style={{ flex: 1, flexDirection: 'row', gap: space.s5, padding: space.s6 }}>
            <View style={{ flex: 1.6, borderWidth: 1, borderColor: colors.borderSoft, borderRadius: radius.lg, overflow: 'hidden' }}>
              <View style={{
                flexDirection: 'row', alignItems: 'center', paddingVertical: space.s3, paddingHorizontal: space.s4,
                borderBottomWidth: 0.5, borderBottomColor: colors.borderSoft, gap: space.s3,
              }}>
                <View style={{ width: 16, height: 16, borderRadius: 4, borderWidth: 1.5, borderColor: colors.borderFirm }} />
                <AppText variant="label" tone="tertiary" style={{ flex: 2 }}>Resident</AppText>
                <AppText variant="label" tone="tertiary" style={{ flex: 1 }}>Flat</AppText>
                <AppText variant="label" tone="tertiary" style={{ flex: 1 }}>Role</AppText>
                <AppText variant="label" tone="tertiary" style={{ flex: 1 }}>Status</AppText>
                <AppText variant="label" tone="tertiary" style={{ flex: 1, textAlign: 'right' }}>Actions</AppText>
              </View>
              {state === 'loading' ? (
                <View style={{ padding: space.s4, gap: space.s3 }}>
                  {[0, 1, 2, 3, 4, 5].map((i) => <Skeleton key={i} h={40} />)}
                </View>
              ) : desktopRows.length === 0 ? (
                <EmptyState
                  icon={<Icon name="users" size={40} color={colors.textTertiary} />}
                  title={state === 'error' ? 'Users unavailable' : 'No users found'}
                  body={state === 'error' ? 'We could not load users right now. Try again in a bit.' : 'Try a different search or filter.'}
                />
              ) : (
                desktopRows.map((u) => {
                  const checked = selected.has(u.id);
                  const actionLabel = !u.approved ? 'Review' : !u.active ? 'Restore' : 'Edit';
                  return (
                    <Pressable
                      key={u.id}
                      onPress={() => router.push(`/admin/users/${u.id}`)}
                      style={({ hovered, pressed }: { hovered?: boolean; pressed: boolean }) => ({
                        flexDirection: 'row', alignItems: 'center', paddingVertical: 10, paddingHorizontal: space.s4,
                        gap: space.s3, borderBottomWidth: 0.5, borderBottomColor: colors.borderSoft,
                        backgroundColor: !u.approved ? colors.accent100 : hovered || pressed ? colors.canvas1 : 'transparent',
                      })}
                    >
                      <Pressable
                        accessibilityRole="checkbox"
                        accessibilityState={{ checked }}
                        onPress={(e) => { e.stopPropagation?.(); toggleSelected(u.id); }}
                        hitSlop={8}
                        style={{
                          width: 16, height: 16, borderRadius: 4, borderWidth: 1.5,
                          borderColor: checked ? colors.primary500 : colors.borderFirm,
                          backgroundColor: checked ? colors.primary500 : 'transparent',
                        }}
                      />
                      <View style={{ flex: 2, flexDirection: 'row', alignItems: 'center', gap: space.s2, minWidth: 0 }}>
                        <Avatar name={u.name} size={32} />
                        <View style={{ minWidth: 0 }}>
                          <AppText variant="bodyMd" weight="600" numberOfLines={1}>{u.name}</AppText>
                          <AppText variant="bodySm" tone="tertiary" numberOfLines={1}>{u.email || '—'}</AppText>
                        </View>
                      </View>
                      <AppText variant="bodySm" mono style={{ flex: 1 }}>{u.flat || '—'}</AppText>
                      <AppText variant="bodySm" style={{ flex: 1 }}>{roleLabel(u)}</AppText>
                      <View style={{ flex: 1 }}>
                        {!u.approved
                          ? <Pill status="pending" label="PENDING" />
                          : !u.active
                            ? <Pill status="rejected" label="INACTIVE" />
                            : <Pill status="resolved" label="ACTIVE" />}
                      </View>
                      <AppText variant="bodySm" weight="600" tone="brand" style={{ flex: 1, textAlign: 'right' }}>{actionLabel}</AppText>
                    </Pressable>
                  );
                })
              )}
            </View>

            <View style={{ width: 320, gap: space.s4 }}>
              <Card>
                <AppText variant="label" tone="tertiary">Society</AppText>
                <View style={{ marginTop: space.s3, gap: 8 }}>
                  <View style={{ flexDirection: 'row', justifyContent: 'space-between' }}>
                    <AppText variant="bodySm" tone="secondary">Residents</AppText>
                    <AppText variant="bodySm" weight="600" mono>{allRows.length}</AppText>
                  </View>
                  <View style={{ flexDirection: 'row', justifyContent: 'space-between' }}>
                    <AppText variant="bodySm" tone="secondary">Wings</AppText>
                    <AppText variant="bodySm" weight="600" mono>{wings.join(' · ') || '—'}</AppText>
                  </View>
                  <View style={{ flexDirection: 'row', justifyContent: 'space-between' }}>
                    <AppText variant="bodySm" tone="secondary">Flats</AppText>
                    <AppText variant="bodySm" weight="600" mono>{flatsCount || '—'}</AppText>
                  </View>
                </View>
              </Card>
              <Card>
                <AppText variant="label" tone="tertiary">By role</AppText>
                <View style={{ marginTop: space.s3, gap: 8 }}>
                  {(Object.entries(roleCounts) as Array<[string, number]>).map(([label, count]) => (
                    <View key={label} style={{ flexDirection: 'row', justifyContent: 'space-between' }}>
                      <AppText variant="bodySm" tone="secondary">{label}s</AppText>
                      <AppText variant="bodySm" weight="600" mono>{count}</AppText>
                    </View>
                  ))}
                </View>
              </Card>
            </View>
          </View>
        </View>
      </View>
    );
  }

  /* ---------------- mobile ---------------- */
  return (
    <Screen scroll={false}>
      <TopBar title="Users" onBack={goBack} />
      <View style={{ paddingHorizontal: space.s4, paddingTop: space.s3 }}>
        <Input value={q} onChangeText={setQ} placeholder="Search name or flat" />
      </View>
      <ChipsRow>
        {FILTERS.map((f) => (
          <Chip key={f} label={f} active={filter === f} onPress={() => setFilter(f)} />
        ))}
      </ChipsRow>
      <Screen padded>
        {state === 'loading' ? (
          <View style={{ gap: space.s3 }}>
            {[0, 1, 2, 3, 4].map((i) => <Skeleton key={i} h={56} />)}
          </View>
        ) : mobileRows.length === 0 ? (
          <EmptyState
            icon={<Icon name="users" size={40} color={colors.textTertiary} />}
            title={state === 'error' ? 'Users unavailable' : 'No users found'}
            body={state === 'error'
              ? 'We could not load users right now. Try again in a bit.'
              : 'Try a different search or filter.'}
          />
        ) : (
          mobileRows.map((u) => (
            <View key={u.id}>
              <Pressable
                accessibilityRole="button"
                onPress={() => router.push(`/admin/users/${u.id}`)}
                style={({ pressed }) => ({
                  flexDirection: 'row', alignItems: 'center', gap: space.s3, paddingVertical: space.s3,
                  backgroundColor: pressed ? colors.canvas1 : 'transparent',
                })}
              >
                <Avatar name={u.name} size={40} />
                <View style={{ flex: 1, minWidth: 0 }}>
                  <AppText variant="bodyMd" weight="500">{u.name}</AppText>
                  <AppText variant="bodySm" tone="tertiary" style={{ marginTop: 2 }}>
                    {[u.flat, roleLabel(u)].filter(Boolean).join(' · ')}
                  </AppText>
                </View>
                {!u.approved
                  ? <Pill status="pending" label="PENDING" />
                  : u.active ? <Pill status="resolved" label="ACTIVE" /> : <Pill status="rejected" label="OFF" />}
                <Icon name="chevron-right" size={18} color={colors.textTertiary} />
              </Pressable>
              <Hairline inset={52} />
            </View>
          ))
        )}
      </Screen>
    </Screen>
  );
}
